import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { withSnackbar } from 'notistack';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { TextField } from '@iris-platform/iris-react-ui';
import FormHelperText from '@material-ui/core/FormHelperText';
import Button from '@material-ui/core/Button';
import { withApollo } from 'react-apollo';
import {
  isEmpty,
} from 'ramda';
import { UPDATE_APN_KEY_CERT } from '../queries';
import { stylesToPropType } from '../../../utils';

const styles = () => ({
  root: {
    width: '100%',
  },
  saveButton: {
    borderRadius: '0.125em',
    marginTop: '1em',
    width: '8em',
  },
});

class ApnCertKey extends Component {
  state = {
    loading: true,
    error: null,
    apnsCert: '',
    apnsKey: '',
    isProduction: false,
  }

  reader = null;

  apnsCertRef = React.createRef();

  apnsKeyRef = React.createRef();

  saveChanges = async () => {
    const {
      client,
      appType,
      childAppName,
      domain,
      enqueueSnackbar,
    } = this.props;
    const {
      apnsCert,
      apnsKey,
      isProduction,
    } = this.state;


    const isApnCertInvalid = isEmpty(apnsCert);
    const isApnKeyInvalid = isEmpty(apnsKey);
    const hasError = isApnCertInvalid || isApnKeyInvalid;

    const appDomainClientID = appType === 'child' ? `${domain}-${childAppName}` : domain;
    const apnKeyCert = {
      app_domain_client_id: appDomainClientID,
      domain,
      apns_cert: apnsCert,
      apns_key: apnsKey,
      apns_production: isProduction,
    };

    if (hasError) {
      const update = {
        apnFileError: isApnCertInvalid || isApnKeyInvalid ? new Error('APN files error') : null,
        error: new Error('Please fix all errors before saving'),
      };
      enqueueSnackbar('Failed to register key', { variant: 'error' });
      this.setState(update);
    } else {
      this.setState({ loading: true });

      try {
        await client.mutate({
          mutation: UPDATE_APN_KEY_CERT,
          variables: { apnKeyCert },
        });
        enqueueSnackbar('Key successfully registered', { variant: 'success' });
        this.setState({ loading: false, error: null });
      } catch (error) {
        enqueueSnackbar('Failed to register key', { variant: 'error' });
        this.setState({ error: new Error('Unable to save changes, please try again later') });
      } finally {
        this.setState({
          loading: false,
          apnsCert: '',
          apnsKey: '',
          isProduction: false,
        });

        this.apnsCertRef.current.value = null;
        this.apnsKeyRef.current.value = null;
      }
    }
  }

  handleFileRead = (e) => {
    const content = this.reader.result;
  };

  handleCertFile = (file) => {
    this.reader = new FileReader();
    this.reader.onloadend = this.handleFileRead;
    this.reader.onload = () => {
      const apnsCert = this.reader.result;
      this.setState({ apnsCert });
    };
    this.reader.readAsText(file);
  };

  handleKeyFile = (file) => {
    this.reader = new FileReader();
    this.reader.onloadend = this.handleFileRead;
    this.reader.onload = () => {
      const apnsKey = this.reader.result;
      this.setState({ apnsKey });
    };
    this.reader.readAsText(file);
  };

  toggleProduction = () => this.setState(prevState => ({
    isProduction: !prevState.isProduction, error: null,
  }));

  render() {
    const { classes } = this.props;
    const {
      apnFileError,
      error,
      loading,
      apnsCert,
      apnsKey,
      isProduction,
    } = this.state;

    return (
      <Fragment>
        <form>
          <FormControl className={classes.root} error={!!apnFileError}>
            <TextField
              label="APN Certificate"
              onChange={(event) => {
                this.handleCertFile(event.target.files[0]);
                this.setState({
                  apnFileError: null,
                  error: null,
                });
              }}
              type="file"
              placeholder="Certificate"
              InputLabelProps={{ shrink: true }}
              margin="normal"
              inputRef={this.apnsCertRef}
            />
            {apnFileError && <FormHelperText>{apnFileError.message}</FormHelperText>}
          </FormControl>
          <br />
          <FormControl className={classes.root} error={!!apnFileError}>
            <TextField
              label="APN Key"
              onChange={(event) => {
                this.handleKeyFile(event.target.files[0]);
                this.setState({
                  apnFileError: null,
                  error: null,
                });
              }}
              type="file"
              placeholder="Key"
              InputLabelProps={{ shrink: true }}
              margin="normal"
              inputRef={this.apnsKeyRef}
            />
            {apnFileError && <FormHelperText>{apnFileError.message}</FormHelperText>}
          </FormControl>
          <br />
          <FormControlLabel
            control={(
              <Switch
                checked={isProduction}
                onChange={this.toggleProduction}
                value="enable-selfprovision"
                color="primary"
                disabled={false}
              />
            )}
            label={isProduction ? 'Production' : 'Development'}
          />
          <br />
          <FormControl className={classes.root} error={!!error}>
            <Button
              variant="contained"
              classes={{ root: classes.saveButton }}
              disabled={apnsCert === '' || apnsKey === ''}
              onClick={this.saveChanges}
            >
                Register
            </Button>
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        </form>
      </Fragment>
    );
  }
}

ApnCertKey.propTypes = {
  appType: PropTypes.string.isRequired,
  childAppName: PropTypes.string.isRequired,
  domain: PropTypes.string.isRequired,
  classes: stylesToPropType(styles()).isRequired,
  client: PropTypes.object.isRequired,
};

export default withSnackbar(withStyles(styles)(withApollo(ApnCertKey)));
